import { useEffect, useState } from 'react';
import {
  Button, Chip, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TablePagination, TextField, Typography, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio,
} from '@mui/material';
import { useRouteLoaderData } from 'react-router';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/GlassCard';
import { GlassDialog } from '@/components/GlassDialog';
import { AdminCredentialsDialog } from '@/features/companies/AdminCredentialsDialog';
import type { AdminCredsView } from '@/features/companies/AdminCredentialsDialog';
import { apiErrorMessage } from '@/lib/errors';
import type { UserProfile } from '@/auth/session';
import {
  editCompanyInformation, listCompanyInformation,
  setCompanyInformationStatus, submitCompanyInformation,
} from '@/features/companyInformation/api';
import type {
  CompanyInformation, CompanyType, SubmitCompanyInformationBody,
} from '@/features/companyInformation/types';

const EMPTY: SubmitCompanyInformationBody = {
  nameLa: '', nameEn: '', address: null, addressEn: null,
  tel: null, taxCode: null, map: null, companyType: 'accounting',
};

const STATUS_CHIP = {
  unApprove: { label: 'ລໍຖ້າ', color: 'warning' as const },
  approve: { label: 'ອະນຸມັດແລ້ວ', color: 'success' as const },
  reject: { label: 'ປະຕິເສດ', color: 'error' as const },
};

/// The server derives the tenant database name from nameEn by keeping only
/// ASCII alphanumerics, so a name with none of them cannot be provisioned.
/// Mirrored here for immediate feedback — the server rejects it regardless.
const nameEnIsUsable = (v: string) => /[a-zA-Z0-9]/.test(v);

export default function CompanyInformationPage() {
  const user = useRouteLoaderData('protected') as UserProfile | undefined;
  const isAdmin = user?.role === 'admin';

  const [rows, setRows] = useState<CompanyInformation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [form, setForm] = useState<SubmitCompanyInformationBody>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState<AdminCredsView | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The id of the row currently being approved/rejected — provisioning takes
  // seconds, so its own approve/reject buttons stay disabled until it settles.
  const [rulingId, setRulingId] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const res = await listCompanyInformation(page + 1, 20);
        if (active) { setRows(res.data); setTotal(res.total); }
      } catch {
        // Leave the previous rows on screen rather than blanking the page.
      }
    }
    void load();
    return () => { active = false; };
  }, [page, reloadKey]);

  function openCreate() { setForm(EMPTY); setEditingId(null); setDialogOpen(true); }

  function openEdit(r: CompanyInformation) {
    setForm({
      nameLa: r.nameLa, nameEn: r.nameEn, address: r.address, addressEn: r.addressEn,
      tel: r.tel, taxCode: r.taxCode, map: r.map, companyType: r.companyType,
    });
    setEditingId(r.id);
    setDialogOpen(true);
  }

  // A row is editable by its own submitter only while nobody has ruled on it.
  // The server enforces the same rule; this only decides what to render.
  const canEdit = (r: CompanyInformation) =>
    isAdmin || (r.userId === user?.id && r.status === 'unApprove');

  async function save() {
    setSaving(true);
    try {
      await toast.promise(
        editingId == null ? submitCompanyInformation(form) : editCompanyInformation(editingId, form),
        {
          loading: 'ກຳລັງບັນທຶກ...', success: 'ບັນທຶກສຳເລັດ',
          error: (e) => apiErrorMessage(e, 'ບັນທຶກບໍ່ສຳເລັດ'),
        },
      );
      setDialogOpen(false);
      setReloadKey((k) => k + 1);
    } catch {
      // toast.promise already surfaced it
    } finally {
      setSaving(false);
    }
  }

  async function rule(r: CompanyInformation, status: 'approve' | 'reject') {
    setRulingId(r.id);
    try {
      const res = await toast.promise(
        setCompanyInformationStatus(r.id, status),
        {
          loading: status === 'approve' ? 'ກຳລັງສ້າງບໍລິສັດ...' : 'ກຳລັງບັນທຶກ...',
          success: status === 'approve' ? 'ອະນຸມັດສຳເລັດ' : 'ປະຕິເສດແລ້ວ',
          error: (e) => apiErrorMessage(e, 'ບໍ່ສຳເລັດ'),
        },
      );
      // Only an approve provisions, and only then are there credentials.
      if ('adminUsername' in res) {
        setCreds({
          username: res.adminUsername, password: res.adminPassword, created: true,
          nameLa: r.nameLa, nameEn: r.nameEn, pathName: res.pathName,
        });
      }
    } catch {
      // toast.promise already displayed the error toast — 409 means someone
      // already ruled on it, so just reload rather than leaving a dead button.
    } finally {
      setRulingId(null);
      setReloadKey((k) => k + 1);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h3">ຂໍ້ມູນບໍລິສັດ</Typography>
        <Button variant="contained" onClick={openCreate}>ເພີ່ມຂໍ້ມູນບໍລິສັດ</Button>
      </Stack>

      <GlassCard>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ຊື່ (ລາວ)</TableCell>
              <TableCell>ຊື່ (ອັງກິດ)</TableCell>
              <TableCell>TIN</TableCell>
              <TableCell>ເບີໂທ</TableCell>
              <TableCell>ປະເພດ</TableCell>
              <TableCell>ສະຖານະ</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              // The server passes any stored status string through as-is; a
              // hand-edited or legacy row can carry one this build doesn't
              // recognise. Fall back rather than let the lookup throw.
              const chip = STATUS_CHIP[r.status] ?? STATUS_CHIP.unApprove;
              return (
                <TableRow key={r.id}>
                  <TableCell>{r.nameLa}</TableCell>
                  <TableCell>{r.nameEn}</TableCell>
                  <TableCell>{r.taxCode ?? '—'}</TableCell>
                  <TableCell>{r.tel ?? '—'}</TableCell>
                  <TableCell>{r.companyType === 'tax' ? 'ພາສີ' : 'ບັນຊີ'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={chip.label} color={chip.color} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                      {canEdit(r) && <Button size="small" onClick={() => openEdit(r)}>ແກ້ໄຂ</Button>}
                      {/* approve is terminal: an approved row offers no transition */}
                      {isAdmin && r.status !== 'approve' && (
                        <Button size="small" variant="contained" disabled={rulingId === r.id}
                          onClick={() => { void rule(r, 'approve'); }}>
                          ອະນຸມັດ
                        </Button>
                      )}
                      {isAdmin && r.status === 'unApprove' && (
                        <Button size="small" color="error" disabled={rulingId === r.id}
                          onClick={() => { void rule(r, 'reject'); }}>
                          ປະຕິເສດ
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={20}
          rowsPerPageOptions={[20]}
        />
      </GlassCard>

      <GlassDialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>ຂໍ້ມູນບໍລິສັດ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="ຊື່ (ລາວ)" value={form.nameLa} required fullWidth
              onChange={(e) => setForm({ ...form, nameLa: e.target.value })} />
            <TextField label="ຊື່ (ອັງກິດ)" value={form.nameEn} required fullWidth
              error={form.nameEn.length > 0 && !nameEnIsUsable(form.nameEn)}
              helperText="ຊື່ອັງກິດໃຊ້ສ້າງຊື່ຖານຂໍ້ມູນ — ຕ້ອງມີ a-z ຫຼື 0-9 ຢ່າງໜ້ອຍ 1 ຕົວ"
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
            <TextField label="ທີ່ຢູ່" value={form.address ?? ''} fullWidth
              onChange={(e) => setForm({ ...form, address: e.target.value || null })} />
            <TextField label="ທີ່ຢູ່ (ອັງກິດ)" value={form.addressEn ?? ''} fullWidth
              onChange={(e) => setForm({ ...form, addressEn: e.target.value || null })} />
            <TextField label="ເບີໂທ" value={form.tel ?? ''} fullWidth
              onChange={(e) => setForm({ ...form, tel: e.target.value || null })} />
            <TextField label="TIN" value={form.taxCode ?? ''} fullWidth
              onChange={(e) => setForm({ ...form, taxCode: e.target.value || null })} />
            <TextField label="ແຜນທີ່ (Google Map)" value={form.map ?? ''} fullWidth
              onChange={(e) => setForm({ ...form, map: e.target.value || null })} />
            <FormControl>
              <FormLabel>ປະເພດ</FormLabel>
              <RadioGroup row value={form.companyType}
                onChange={(e) => setForm({ ...form, companyType: e.target.value as CompanyType })}>
                <FormControlLabel value="accounting" control={<Radio />} label="ບັນຊີ" />
                <FormControlLabel value="tax" control={<Radio />} label="ພາສີ" />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">ຍົກເລີກ</Button>
          <Button variant="contained" loading={saving}
            disabled={!form.nameLa.trim() || !nameEnIsUsable(form.nameEn)}
            onClick={() => { void save(); }}>
            ບັນທຶກ
          </Button>
        </DialogActions>
      </GlassDialog>

      <AdminCredentialsDialog creds={creds} open={creds != null} onClose={() => setCreds(null)} />
    </Stack>
  );
}
